import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import {
	DialogActions,
	DialogContent,
	DialogTitle,
	Button,
	Grid,
	IconButton,
	Tooltip,
	type Breakpoint
} from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import { type DetailsPageContentProps } from 'app/main/components/page-layouts/details/DetailsPage';
import { alertSuccessMsg, openLink } from 'app/main/components/Utils';
import useModel, {
	type ModelEventsParams,
	type onAfterSaveType,
	type onBeforeSaveType
} from 'app/main/components/hooks/useModel';
import useSettings from 'app/main/components/hooks/useSettings';
import Form from 'app/main/components/Form';
import Icon from 'app/main/components/Icon';
import UnsavedDialog from 'app/main/components/dialogs/UnsavedDialog';

/* eslint-disable-next-line import/no-cycle */
import FastAddDialogSettingsManager from './FastAddDialogSettingsManager';

export interface FastAddDialogSettingsType {
	content: React.FC<DetailsPageContentProps>;
	events?: {
		onBeforeSave?: onBeforeSaveType;
		onAfterSave?: onAfterSaveType;
	};
	showOpenFullDetails?: boolean;
}

interface FastAddDialogProps {
	/** The module name */
	module: string;
	/** An object containing the base modified model */
	model?: any;
	/** If true, the dialog is in edit mode */
	isUpdate?: boolean;
	/** The max dialog width breakpoint */
	maxDialogWidth?: Breakpoint;
	/** A function that, if provided, is called after the save operation and refresh the related view */
	updateRefresh?: VoidFunction;
	/** A callback called on dialog close */
	onDialogClose?: VoidFunction;
	/** A callback called on model save */
	onAfterSaveHandler?: (params: ModelEventsParams) => void;
	/** An object containing additional props provided to the content component */
	additionalProps?: any;
}

export const showFastAddDialog = ({
	maxDialogWidth = 'md',
	...props
}: {
	maxDialogWidth?: Breakpoint;
} & FastAddDialogProps) =>
	openDialog({
		children: <FastAddDialog {...props} />,
		maxWidth: maxDialogWidth,
		fullWidth: true,
		scroll: 'body',
		onClose: (_event, reason) => {
			if (reason === 'escapeKeyDown') {
				const closeEvent = new Event('onFastAddDialogClose');
				document.dispatchEvent(closeEvent);
				props.onDialogClose?.();
			}
		}
	});

/** This dialog allows you to perform an add action without having being redirected to another page and manually go back to the original one */
export default function FastAddDialog(props: FastAddDialogProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings(props.module);

	const dialogSettings: FastAddDialogSettingsType | undefined = FastAddDialogSettingsManager[props.module];

	const idField = `${settings.modu_short}_uid`;

	const handleDialogClose = () => {
		props.onDialogClose?.();
		dispatch(closeDialog());
	};

	const modelProps = useModel({
		model: props.isUpdate ? props.model : undefined,
		baseModel: { [idField]: props.isUpdate ? props.model[idField] : uuidv4() },
		baseModifiedModel: !props.isUpdate ? props.model : undefined,
		settings,
		isEdit: true,
		isAdd: !props.isUpdate
	});

	const sendData = () => {
		modelProps.save({
			onBeforeSave: dialogSettings?.events?.onBeforeSave,
			onAfterSave: dialogSettings?.events?.onAfterSave,
			onSuccess: params => {
				dispatch(alertSuccessMsg(t, !props.isUpdate ? 'ALERT_ADD_SUCCESS' : 'ALERT_UPDATE_SUCCESS'));
				props.onAfterSaveHandler?.({ ...params });
				handleDialogClose();
				props.updateRefresh?.();
			}
		});
	};

	const openFullDetailsEdit = () => {
		handleDialogClose();
		const moduleToUrl = props.module?.replaceAll('_', '/');
		openLink({
			pathname: props.isUpdate
				? `/${moduleToUrl}/details/${modelProps.model[idField]}/summary?edit=true`
				: `/${moduleToUrl}/add`,
			state: { model: modelProps.model }
		});
	};

	return (
		<>
			<UnsavedDialog
				hasModelChanged={modelProps.hasModelChanged}
				eventName="onFastAddDialogClose"
				onClose={handleDialogClose}
			/>
			<DialogTitle>
				<FormattedMessage
					id="fastAddDialog.title"
					defaultMessage={t(!props.isUpdate ? 'DIALOG_TITLE_ADD' : 'DIALOG_TITLE_UPDATE')}
					values={{ module: t(`MODULE_${props.module}_NAME_SINGULAR`) }}
				/>
				{dialogSettings?.showOpenFullDetails && (
					<Tooltip title={t('DIALOG_ACTION_OPEN_FULL_DETAILS')} arrow>
						<IconButton className="absolute right-16 top-12" onClick={openFullDetailsEdit}>
							<Icon icon="square-edit-outline" />
						</IconButton>
					</Tooltip>
				)}
			</DialogTitle>
			<DialogContent>
				<Form id="fastAddDialog" onSubmit={sendData}>
					<Grid container spacing={1}>
						{dialogSettings?.content && (
							<dialogSettings.content
								model={modelProps.model}
								isEdit
								isAdd={!props.isUpdate}
								sendChange={modelProps.sendChange}
								{...props.additionalProps}
							/>
						)}
					</Grid>
				</Form>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={() => {
						const closeEvent = new Event('onFastAddDialogClose');
						document.dispatchEvent(closeEvent);
					}}
				>
					{t('DIALOG_ACTION_CANCEL')}
				</Button>
				<Button type="submit" form="fastAddDialog">
					{t('DIALOG_ACTION_CONFIRM')}
				</Button>
			</DialogActions>
		</>
	);
}
